import React from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import FocusGuard from './FocusGuard';

/* ─────────────────────────────────────────────
   ICONS  (inline SVG — no extra deps needed)
───────────────────────────────────────────── */

const IconArrow = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden="true">
    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

const IconBank = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-blue-400" aria-hidden="true">
    <path d="M3 10l9-6 9 6" />
    <path d="M5 10v8M9.5 10v8M14.5 10v8M19 10v8" />
    <path d="M3 18h18M3 21h18" />
  </svg>
);

const IconSync = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-blue-400" aria-hidden="true">
    <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const IconChat = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-blue-400" aria-hidden="true">
    <path d="M21 12a8 8 0 01-8 8H4l1.5-3A8 8 0 1121 12z" />
    <path d="M9 11h.01M12 11h.01M15 11h.01" />
  </svg>
);

const IconSend = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 text-white" aria-hidden="true">
    <path d="M3.4 20.4l17.45-7.48a1 1 0 000-1.84L3.4 3.6a.993.993 0 00-1.39.91L2 9.12c0 .5.37.93.87.99L17 12 2.87 13.88c-.5.07-.87.5-.87 1l.01 4.61c0 .71.73 1.2 1.39.91z" />
  </svg>
);

/* ─────────────────────────────────────────────
   NAVBAR
───────────────────────────────────────────── */

const Navbar: React.FC = () => (
  <nav className="fixed top-0 left-0 right-0 z-50 bg-white/5 backdrop-blur-md border-b border-white/10">
    <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
      {/* Logo */}
      <div className="flex items-center gap-3">
        <span className="text-xl font-bold bg-gradient-to-r from-white to-blue-300 bg-clip-text text-transparent tracking-tight">
          AskYour<span className="text-blue-400">Cash</span>
        </span>
      </div>

      {/* Nav links */}
      <div className="hidden md:flex items-center gap-8">
        <a
          href="#features"
          className="text-slate-400 hover:text-white text-sm transition-colors duration-150"
        >
          Features
        </a>
        <a
          href="#how-it-works"
          className="text-slate-400 hover:text-white text-sm transition-colors duration-150"
        >
          How it works
        </a>
        <Link
          to="/login"
          className="text-sm font-semibold text-white border border-white/20 hover:border-white/40 hover:bg-white/5 px-4 py-2 rounded-full transition-all duration-200"
        >
          Sign In
        </Link>
      </div>

      {/* Mobile Sign In */}
      <Link
        to="/login"
        className="md:hidden text-sm font-semibold text-white border border-white/20 px-4 py-2 rounded-full"
      >
        Sign In
      </Link>
    </div>
  </nav>
);

/* ─────────────────────────────────────────────
   CHAT MOCKUP
   A realistic mini version of the product: the
   user asks Finn a question, Finn analyzes real
   transactions and answers with numbers.
───────────────────────────────────────────── */

// Mirrors a real Finn answer: voice and chart match the actual /ai/query
// output (get_spending_by_category + generate_chart → CategorySpendingChart,
// a donut chart with a dot legend titled "Spending by Category").
const donutSegments = [
  { label: 'Rent & Utilities', amount: '$1,665.49', frac: 0.626, stroke: 'stroke-blue-500', dot: 'bg-blue-500' },
  { label: 'Food & Drink', amount: '$798.00', frac: 0.3, stroke: 'stroke-blue-400', dot: 'bg-blue-400' },
  { label: 'Transportation', amount: '$123.40', frac: 0.046, stroke: 'stroke-blue-300', dot: 'bg-blue-300' },
  { label: 'Other', amount: '$74.32', frac: 0.028, stroke: 'stroke-slate-500', dot: 'bg-slate-500' },
];
const DONUT_C = 2 * Math.PI * 28;
let donutAcc = 0;
const donutArcs = donutSegments.map((seg) => {
  const len = seg.frac * DONUT_C;
  const arc = { ...seg, len, start: donutAcc };
  donutAcc += len;
  return arc;
});

const CategoryMiniChart = () => (
  <div className="mt-3">
    <p className="text-[10px] text-slate-400 mb-1.5">Spending by Category</p>
    <div className="flex items-center gap-4">
      <svg viewBox="0 0 80 80" className="w-20 h-20 shrink-0">
        <g transform="rotate(-90 40 40)">
          {donutArcs.map((arc) => (
            // -2 approximates the real chart's paddingAngle={2} gap; clamp keeps tiny slices visible
            <circle
              key={arc.label}
              cx="40" cy="40" r="28" fill="none" strokeWidth="12"
              className={arc.stroke}
              strokeDasharray={`${Math.max(arc.len - 2, 1)} ${DONUT_C - Math.max(arc.len - 2, 1)}`}
              strokeDashoffset={-arc.start}
            />
          ))}
        </g>
      </svg>
      <div className="flex-1 flex flex-col gap-1.5">
        {donutSegments.map((seg) => (
          <div key={seg.label} className="flex items-center justify-between text-[11px] gap-2">
            <span className="flex items-center gap-1.5 text-slate-400 truncate">
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${seg.dot}`} />
              {seg.label}
            </span>
            <span className="text-slate-300 shrink-0">{seg.amount}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ChatMockup: React.FC = () => (
  <div
    className="relative w-full max-w-md mx-auto select-none py-8"
    role="img"
    aria-label="Example conversation: a user asks Finn where their money went last month; Finn replies they spent a total of $2,661.21, with a donut chart breaking spending down across Rent & Utilities, Food & Drink, Transportation, and other categories. Follow-up questions get instant answers: $486.00 on groceries across 11 transactions, and $67.00 at Starbucks across 12 transactions."
  >
    {/* Chat card */}
    <div className="bg-slate-800/80 backdrop-blur-xl border border-slate-700/60 rounded-2xl shadow-2xl shadow-blue-950/60 overflow-hidden">
      {/* Header — mirrors the real app's assistant header */}
      <div className="flex items-center px-5 py-3.5 border-b border-slate-700/60">
        <span className="text-white text-sm font-semibold">✨ Finn</span>
      </div>

      <div className="p-5 flex flex-col gap-3">
        {/* Exchange 1: category spending — answer text and chart mirror the
            real /ai/query response (run against the live app) */}
        <div
          className="self-end max-w-[85%] bg-blue-600 text-white text-sm rounded-2xl rounded-br-md px-4 py-2.5 opacity-0 animate-fade-up"
          style={{ animationDelay: '0.15s' }}
        >
          Where did my money go last month?
        </div>

        <div
          className="self-start max-w-[92%] bg-slate-700/70 rounded-2xl rounded-bl-md p-4 opacity-0 animate-fade-up"
          style={{ animationDelay: '0.5s' }}
        >
          <p className="text-sm text-slate-200 leading-relaxed">
            Last month, Alex, you spent a total of <strong className="text-white">$2,661.21</strong>.
            Here&rsquo;s a breakdown of your spending by category:
          </p>
          <CategoryMiniChart />
        </div>

        {/* Exchange 2: category follow-up */}
        <div
          className="self-end max-w-[85%] bg-blue-600 text-white text-sm rounded-2xl rounded-br-md px-4 py-2.5 opacity-0 animate-fade-up"
          style={{ animationDelay: '1.2s' }}
        >
          How much on groceries?
        </div>

        <div
          className="self-start max-w-[92%] bg-slate-700/70 text-sm text-slate-200 leading-relaxed rounded-2xl rounded-bl-md px-4 py-2.5 opacity-0 animate-fade-up"
          style={{ animationDelay: '1.5s' }}
        >
          Last month, you spent a total of <strong className="text-white">$486.00</strong> on
          groceries across 11 transactions.
        </div>

        {/* Exchange 3: merchant follow-up */}
        <div
          className="self-end max-w-[85%] bg-blue-600 text-white text-sm rounded-2xl rounded-br-md px-4 py-2.5 opacity-0 animate-fade-up"
          style={{ animationDelay: '1.8s' }}
        >
          And at Starbucks?
        </div>

        <div
          className="self-start max-w-[92%] bg-slate-700/70 text-sm text-slate-200 leading-relaxed rounded-2xl rounded-bl-md px-4 py-2.5 opacity-0 animate-fade-up"
          style={{ animationDelay: '2.1s' }}
        >
          Last month, you spent a total of <strong className="text-white">$67.00</strong> at
          Starbucks across 12 transactions.
        </div>

        {/* Input footer — visual only */}
        <div className="mt-1 flex items-center justify-between rounded-full bg-slate-900/60 border border-slate-700/60 px-4 py-2.5">
          <span className="text-slate-500 text-sm">Ask Finn about your money…</span>
          <span className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
            <IconSend />
          </span>
        </div>
      </div>
    </div>

    {/* Floating chip: budget alert */}
    <div
      className="absolute -top-2 -right-2 bg-slate-800/95 backdrop-blur-xl border border-slate-600/60 rounded-xl px-3.5 py-2.5 shadow-xl animate-float"
      aria-hidden="true"
    >
      <p className="text-[9px] text-slate-400 uppercase tracking-widest mb-0.5">Budget alert</p>
      <p className="text-white font-semibold text-xs leading-tight">
        Dining at <span className="text-amber-400">82%</span> of $600
      </p>
      <div className="mt-1.5 h-1 w-24 rounded-full bg-slate-700 overflow-hidden">
        <div className="h-full w-[82%] rounded-full bg-amber-400" />
      </div>
    </div>

    {/* Floating chip: goal progress */}
    <div
      className="absolute -bottom-2 -left-2 flex items-center gap-2.5 bg-slate-800/95 backdrop-blur-xl border border-slate-600/60 rounded-xl px-3.5 py-2.5 shadow-xl animate-float-slow"
      aria-hidden="true"
    >
      <svg viewBox="0 0 28 28" className="w-7 h-7 -rotate-90">
        <circle cx="14" cy="14" r="10" fill="none" strokeWidth="4" className="stroke-slate-700" />
        <circle
          cx="14" cy="14" r="10" fill="none" strokeWidth="4" strokeLinecap="round"
          className="stroke-blue-400"
          strokeDasharray={62.8}
          strokeDashoffset={62.8 * 0.36}
        />
      </svg>
      <div>
        <p className="text-[9px] text-slate-400 uppercase tracking-widest mb-0.5">Goal</p>
        <p className="text-white font-semibold text-xs leading-tight">
          Emergency fund · <span className="text-blue-400">64%</span> funded
        </p>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   HERO SECTION
───────────────────────────────────────────── */

const HeroSection: React.FC = () => (
  <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 pt-20">
    {/* Decorative blobs */}
    <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl pointer-events-none" />
    <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-blue-400/10 blur-3xl pointer-events-none" />

    <div className="max-w-7xl mx-auto px-6 w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center py-20">
      {/* Left: copy */}
      <div className="flex flex-col gap-6">
        {/* Micro-label */}
        <p className="text-blue-400 text-xs tracking-[0.2em] uppercase font-semibold">
          Meet Finn &nbsp;·&nbsp; Your finance assistant
        </p>

        {/* Headline */}
        <h1 className="text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight pb-2 -mb-2 bg-gradient-to-br from-white via-slate-100 to-blue-200 bg-clip-text text-transparent">
          Ask your money anything.
        </h1>

        {/* Subheadline */}
        <p className="text-slate-400 text-lg leading-relaxed max-w-lg">
          Connect your bank once. Finn syncs and categorizes every transaction, then answers
          real questions&nbsp;— &ldquo;Where did my money go last month?&rdquo;&nbsp;—
          with actual numbers, budget alerts, and goal check-ins.
        </p>

        {/* CTAs */}
        <div className="flex flex-wrap gap-4 mt-2">
          <Link to="/register" className="btn-primary inline-flex items-center gap-2">
            Get Started
            <IconArrow />
          </Link>
          <a href="#how-it-works" className="btn-outline inline-flex items-center gap-2">
            See how it works
          </a>
        </div>

        {/* Footnote */}
        <p className="text-slate-500 text-sm">
          Secure, read-only bank connection via Plaid. Finn is a financial assistant, not a licensed advisor.
        </p>
      </div>

      {/* Right: chat mockup */}
      <div className="w-full h-full flex items-center justify-center">
        <ChatMockup />
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────
   HOW IT WORKS
───────────────────────────────────────────── */

const steps = [
  {
    icon: <IconBank />,
    title: 'Connect your bank',
    description:
      'Link checking, savings, and credit cards through Plaid. Read-only — AskYourCash can never move money.',
  },
  {
    icon: <IconSync />,
    title: 'Finn syncs and categorizes',
    description:
      'Every transaction imports and auto-categorizes on its own. There is no manual entry — not even an ‘add transaction’ button.',
  },
  {
    icon: <IconChat />,
    title: 'Ask anything',
    description:
      'Budgets, goals, unusual charges, charts — it’s all one question away in chat.',
  },
];

const HowItWorks: React.FC = () => (
  <section id="how-it-works" className="py-24 bg-slate-900/50 relative overflow-hidden scroll-mt-24">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-64 bg-blue-900/20 blur-3xl pointer-events-none" />

    <div className="max-w-7xl mx-auto px-6 relative">
      {/* Section header */}
      <div className="text-center mb-16">
        <p className="text-blue-400 text-xs tracking-[0.2em] uppercase font-semibold mb-3">
          Three steps
        </p>
        <h2 className="text-3xl lg:text-4xl font-bold text-white">
          From bank connection to first answer in minutes
        </h2>
      </div>

      {/* Steps */}
      <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Connecting line (desktop) */}
        <div
          className="hidden lg:block absolute top-12 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent pointer-events-none"
          aria-hidden="true"
        />

        {steps.map((step, i) => (
          <div key={step.title} className="glass-card p-8 relative">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-400 font-bold text-sm flex items-center justify-center">
                {i + 1}
              </span>
              {step.icon}
            </div>
            <h3 className="text-white font-semibold text-xl mb-3">{step.title}</h3>
            <p className="text-slate-400 text-sm leading-relaxed">{step.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────
   FEATURES
   Each card leads with a small, realistic
   vignette of the product instead of an icon.
───────────────────────────────────────────── */

const RING_CIRCUMFERENCE = 2 * Math.PI * 26; // ≈ 163.4

const VignetteTransactions = () => (
  <div className="flex flex-col justify-center gap-2 text-xs">
    <div className="flex items-center gap-2">
      <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
      <span className="text-slate-300 flex-1 truncate">Blue Bottle Coffee · <span className="text-slate-500">Dining</span></span>
      <span className="text-slate-300">$6.40</span>
    </div>
    <div className="border-t border-white/5" />
    <div className="flex items-center gap-2">
      <span className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0" />
      <span className="text-slate-300 flex-1 truncate">Whole Foods · <span className="text-slate-500">Groceries</span></span>
      <span className="text-slate-300">$84.12</span>
    </div>
  </div>
);

const VignetteBudget = () => (
  <div className="flex flex-col justify-center gap-2">
    <div className="flex items-center justify-between text-xs">
      <span className="text-slate-300">Dining</span>
      <span className="text-slate-400">$492 <span className="text-slate-600">/ $600</span></span>
    </div>
    <div className="relative h-2 rounded-full bg-slate-700 overflow-hidden">
      <div
        className="h-full w-[82%] rounded-full bg-amber-400 origin-left animate-grow-x"
        style={{ animationDelay: '0.3s' }}
      />
      <div className="absolute top-0 bottom-0 left-[80%] w-px bg-white/40" />
    </div>
    <p className="text-[10px] text-amber-400/90">Alert sent at 80%</p>
  </div>
);

const VignetteGoal = () => (
  <div className="flex items-center gap-4">
    <div className="relative w-16 h-16 shrink-0">
      <svg viewBox="0 0 64 64" className="w-16 h-16 -rotate-90">
        <circle cx="32" cy="32" r="26" fill="none" strokeWidth="6" className="stroke-slate-700" />
        <circle
          cx="32" cy="32" r="26" fill="none" strokeWidth="6" strokeLinecap="round"
          className="stroke-blue-400"
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={RING_CIRCUMFERENCE * 0.36}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold">64%</span>
    </div>
    <div className="text-xs">
      <p className="text-slate-300 font-medium mb-0.5">Emergency fund</p>
      <p className="text-slate-400">$3,200 <span className="text-slate-600">/ $5,000</span></p>
    </div>
  </div>
);

const VignetteAnomaly = () => (
  <div className="flex items-start gap-2.5 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2.5">
    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 shrink-0" />
    <div className="text-xs">
      <p className="text-slate-200 font-medium">New merchant · $89.99</p>
      <p className="text-slate-400 mt-0.5">2.3× your usual for this category</p>
    </div>
  </div>
);

const chartBars = [
  { height: 20, delay: '0.15s', color: 'fill-slate-500/50' },
  { height: 32, delay: '0.3s', color: 'fill-blue-400/70' },
  { height: 16, delay: '0.45s', color: 'fill-slate-500/50' },
  { height: 40, delay: '0.6s', color: 'fill-blue-500/80' },
  { height: 28, delay: '0.75s', color: 'fill-blue-300/70' },
];

const VignetteChart = () => (
  <svg viewBox="0 0 120 48" className="w-full h-16">
    {chartBars.map((bar, i) => (
      <rect
        key={i}
        x={6 + i * 24}
        y={44 - bar.height}
        width={16}
        height={bar.height}
        rx={3}
        className={`chart-bar ${bar.color}`}
        style={{ animationDelay: bar.delay }}
      />
    ))}
  </svg>
);

const VignetteHealthScore = () => (
  <div className="flex flex-col justify-center gap-2.5">
    <p className="text-white font-bold text-3xl leading-none">
      87 <span className="text-slate-500 text-sm font-normal">/ 100</span>
    </p>
    <div className="flex gap-1">
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className="flex-1 h-1.5 rounded-full bg-blue-400" />
      ))}
      <span className="flex-1 h-1.5 rounded-full bg-slate-700 relative overflow-hidden">
        <span className="absolute inset-y-0 left-0 w-1/2 bg-blue-400" />
      </span>
    </div>
  </div>
);

const features = [
  {
    title: 'Spending, explained',
    description:
      'Every transaction auto-categorized. Ask by category, merchant, or month — ‘coffee’ finds your actual coffee shops.',
    vignette: <VignetteTransactions />,
  },
  {
    title: 'Budgets that speak up',
    description:
      'Set per-category limits or let Finn run a 50/30/20 check on your income. You get an alert the moment a category crosses 80%.',
    vignette: <VignetteBudget />,
  },
  {
    title: 'Goals with a reality check',
    description:
      'Name a target and a deadline. Finn checks it against your actual monthly surplus and tells you if the math works.',
    vignette: <VignetteGoal />,
  },
  {
    title: 'Anomaly alerts',
    description:
      'New merchants, duplicate charges, spending spikes — flagged and ranked by severity before you’d ever notice.',
    vignette: <VignetteAnomaly />,
  },
  {
    title: 'Charts on demand',
    description:
      'Ask for your spending trend, net worth, or cash flow and Finn draws the chart right in the chat.',
    vignette: <VignetteChart />,
  },
  {
    title: 'A single health score',
    description:
      'One 0-100 number that summarizes your habits, plus concrete savings opportunities to raise it.',
    vignette: <VignetteHealthScore />,
  },
];

const FeaturesGrid: React.FC = () => (
  <section id="features" className="py-24 relative overflow-hidden scroll-mt-24">
    <div className="max-w-7xl mx-auto px-6 relative">
      {/* Section header */}
      <div className="text-center mb-16">
        <p className="text-blue-400 text-xs tracking-[0.2em] uppercase font-semibold mb-3">
          What Finn does
        </p>
        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
          Real answers, from your real data
        </h2>
        <p className="text-slate-400 text-lg max-w-2xl mx-auto leading-relaxed">
          No dashboards to configure. Finn reads your transactions and does the work — you just ask.
        </p>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((feature) => (
          <div
            key={feature.title}
            className="bg-slate-800/80 backdrop-blur-xl border border-slate-700/60 rounded-2xl p-8 flex flex-col hover:bg-slate-700/80 transition-all duration-300 hover:-translate-y-1"
          >
            {/* Vignette */}
            <div className="h-24 flex flex-col justify-center mb-6 select-none" aria-hidden="true">
              {feature.vignette}
            </div>

            {/* Title */}
            <h3 className="text-white font-semibold text-xl mb-3">{feature.title}</h3>

            {/* Description */}
            <p className="text-slate-400 text-sm leading-relaxed flex-1">
              {feature.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────────
   BOTTOM CTA
───────────────────────────────────────────── */

const CtaBand: React.FC = () => (
  <section className="py-20 bg-slate-900/50">
    <div className="max-w-2xl mx-auto px-6 text-center">
      <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
        Your first answer is one question away.
      </h2>
      <p className="text-slate-400 mb-8">
        Free to start. Connect a bank and ask Finn anything.
      </p>
      <Link to="/register" className="btn-primary inline-flex items-center gap-2">
        Get Started
        <IconArrow />
      </Link>
    </div>
  </section>
);

/* ─────────────────────────────────────────────
   FOOTER
───────────────────────────────────────────── */

const Footer: React.FC = () => (
  <footer className="bg-slate-950 border-t border-white/5 py-10">
    <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <span className="text-lg font-bold bg-gradient-to-r from-white to-blue-300 bg-clip-text text-transparent">
          AskYour<span className="text-blue-400">Cash</span>
        </span>
        <span className="text-slate-600 text-xs">· Chat with Finn about your money</span>
      </div>
      <p className="text-slate-600 text-xs text-center">
        © {new Date().getFullYear()} AskYourCash. Finn is a financial assistant, not a licensed advisor.
      </p>
      <div className="flex gap-6">
        <Link to="/login" className="text-slate-500 hover:text-white text-xs transition-colors">Sign In</Link>
        <Link to="/register" className="text-slate-500 hover:text-white text-xs transition-colors">Sign Up</Link>
      </div>
    </div>
  </footer>
);

/* ─────────────────────────────────────────────
   LANDING PAGE  (root component)
───────────────────────────────────────────── */

const LandingPage: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen flex flex-col">
      <FocusGuard landmarkId="main-content" autoFocusFirst={false} />
      <Navbar />
      <main id="main-content" className="flex-1">
        <HeroSection />
        <HowItWorks />
        <FeaturesGrid />
        <CtaBand />
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
